import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../hooks/redux';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { compressImage } from '../utils/imageCompression';
import { MessageMedia } from '../components/chat/MessageMedia';
import { io, Socket } from 'socket.io-client';

interface Post {
  id: string;
  user_id: number;
  content: string | null;
  media_type: string | null;
  media_urls: Array<{ url: string; mediaType: string; thumbnailUrl?: string }> | null;
  link_preview: { title?: string; description?: string; image?: string; url?: string } | null;
  created_at: string;
  display_name: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  reaction_count: number;
  comment_count: number;
  reaction_types: string[];
}

interface Comment {
  id: string;
  post_id: string;
  user_id: number;
  content: string;
  created_at: string;
  display_name: string;
  first_name: string;
  last_name: string;
  photo_url: string;
}

export const HomePage: React.FC = () => {
  const { loginUser } = useAppSelector((state) => state.user);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<{ [postId: string]: Comment[] }>({});
  const [newComment, setNewComment] = useState<{ [postId: string]: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const socketRef = useRef<Socket | null>(null);

  // Connect to Socket.IO for real-time updates
  useEffect(() => {
    if (!loginUser) return; // Only connect if user is logged in

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
      (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');

    socketRef.current = io(API_BASE_URL, { withCredentials: true });

    // Join the activity-feed room (authenticated users only)
    socketRef.current.emit('join-activity-feed', loginUser.id);

    // Listen for new posts
    socketRef.current.on('new-post', (post: Post) => {
      setPosts((prevPosts) => [post, ...prevPosts]);
    });

    // Listen for reaction updates
    socketRef.current.on('reaction-updated', async ({ postId }: { postId: string }) => {
      // Reload posts to get updated reaction counts
      try {
        const response = await axios.get('/api/posts', { withCredentials: true });
        if (response.data.success) {
          setPosts(response.data.data.posts);
        }
      } catch (error) {
        console.error('Failed to reload posts:', error);
      }
    });

    // Listen for new comments — de-duplicate by id so the poster doesn't see it twice
    socketRef.current.on('new-comment', ({ postId, comment }: { postId: string; comment: Comment }) => {
      setComments((prev) => {
        const existing = prev[postId] || [];
        if (existing.some(c => c.id === comment.id)) return prev;
        return { ...prev, [postId]: [...existing, comment] };
      });
      setPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p
        )
      );
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [loginUser?.id]); // Only reconnect if user ID changes (login/logout)

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/posts', { withCredentials: true });
      if (response.data.success) {
        setPosts(response.data.data.posts);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(files);

    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && selectedFiles.length === 0) return;

    try {
      setUploading(true);
      let mediaUrls = null;

      // Upload media files if any
      if (selectedFiles.length > 0) {
        const formData = new FormData();

        for (const file of selectedFiles) {
          let fileToUpload = file;

          // Compress images. Use the shared helper — it disables the web worker
          // (CSP blocks it in this app and silently falls back to no compression
          // otherwise), tightens quality, and gracefully returns the original
          // file if compression throws so the upload never fails outright.
          if (file.type.startsWith('image/')) {
            fileToUpload = await compressImage(file, {
              maxSizeMB: 0.8,         // ~800 KB target; was 1 MB and many photos were sneaking through above 5 MB
              maxWidthOrHeight: 1600, // 1600px is plenty for a phone screen feed
              useWebWorker: false,    // forced — CSP blocks workers on this app
              quality: 0.82,
            });
          }

          formData.append('media', fileToUpload);
        }

        const uploadResponse = await axios.post('/api/posts/upload-media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });

        if (uploadResponse.data.success) {
          mediaUrls = uploadResponse.data.data.mediaUrls;
        }
      }

      // Create post
      const postData = {
        id: crypto.randomUUID(),
        content: newPostContent.trim() || null,
        mediaType: mediaUrls && mediaUrls.length > 0 ? (mediaUrls.length > 1 ? 'multiple' : mediaUrls[0].mediaType) : null,
        mediaUrls,
        linkPreview: null
      };

      const response = await axios.post('/api/posts', postData, { withCredentials: true });

      if (response.data.success) {
        setPosts([response.data.data.post, ...posts]);
        setNewPostContent('');
        setSelectedFiles([]);
        previewUrls.forEach(url => URL.revokeObjectURL(url));
        setPreviewUrls([]);
        setShowPostModal(false);
      }
    } catch (error) {
      console.error('Failed to create post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;

    try {
      await axios.delete(`/api/posts/${postId}`, { withCredentials: true });
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  const handleReaction = async (postId: string, reactionType: string) => {
    try {
      const response = await axios.post(`/api/posts/${postId}/reactions`, { reactionType }, { withCredentials: true });

      if (response.data.success) {
        // Update the post in state immediately
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === postId) {
            const action = response.data.data.action;
            return {
              ...post,
              reaction_count: action === 'added' ? post.reaction_count + 1 : Math.max(0, post.reaction_count - 1)
            };
          }
          return post;
        }));
      }
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const loadComments = async (postId: string) => {
    try {
      const response = await axios.get(`/api/posts/${postId}/comments`, { withCredentials: true });

      if (response.data.success) {
        setComments(prev => ({ ...prev, [postId]: response.data.data.comments }));
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const toggleComments = (postId: string) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
      if (!comments[postId]) {
        loadComments(postId);
      }
    }
    setExpandedComments(newExpanded);
  };

  const handleAddComment = async (postId: string) => {
    const content = newComment[postId];
    if (!content?.trim()) return;

    try {
      const response = await axios.post(`/api/posts/${postId}/comments`, {
        id: crypto.randomUUID(),
        content: content.trim()
      }, { withCredentials: true });

      if (response.data.success) {
        setNewComment(prev => ({ ...prev, [postId]: '' }));
        // Comment appears via socket 'new-comment' event (de-duplicated by id)
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const formatTime = (timestamp: string) => {
    // MySQL DATETIME has no timezone — parse as local (no 'Z') to match server timezone
    const dateStr = typeof timestamp === 'string' && !timestamp.includes('T')
      ? timestamp.replace(' ', 'T') : timestamp;
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '16px', paddingBottom: '24px' }}>
      {/* Create Post Button */}
      <div className="card glassCard" style={{ marginBottom: '16px' }}>
        <div
          className="card-content"
          onClick={() => setShowPostModal(true)}
          style={{ padding: '16px', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {loginUser?.photo_url ? (
              <img
                src={loginUser.photo_url}
                alt={loginUser.display_name}
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--primary-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {loginUser?.display_name?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <div style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: '24px',
              background: 'var(--background-default)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)'
            }}>
              What's on your mind?
            </div>
          </div>
        </div>
      </div>

      {/* Posts Feed */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="circular-progress">
            <svg width="40" height="40" viewBox="0 0 40 40">
              <circle className="circular-progress-circle" cx="20" cy="20" r="18" fill="none" strokeWidth="4" />
            </svg>
          </div>
        </div>
      ) : posts.length === 0 ? (
        <div className="card glassCard">
          <div className="card-content" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            <span className="material-icons" style={{ fontSize: '64px', opacity: 0.3 }}>
              article
            </span>
            <div style={{ marginTop: '16px' }}>No posts yet. Be the first to share something!</div>
          </div>
        </div>
      ) : (
        posts.map((post) => (
          <div key={post.id} className="card glassCard" style={{ marginBottom: '16px', position: 'relative' }}>
            <div className="card-content" style={{ padding: '16px' }}>
              {/* Post Header */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                {post.photo_url ? (
                  <img
                    src={post.photo_url}
                    alt={post.display_name}
                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {post.display_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                    {post.display_name || `${post.first_name} ${post.last_name}`}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {formatTime(post.created_at)}
                  </div>
                </div>
              </div>

              {/* Edit/Delete Buttons for post owner or admin/manager */}
              {(String(post.user_id) === String(loginUser?.id) || String(loginUser?.id) === '1' || String(loginUser?.id) === '2') && (
                <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '4px' }}>
                  <button
                    className="icon-btn"
                    onClick={() => handleDeletePost(post.id)}
                    title="Delete post"
                    style={{ color: 'var(--error-color)' }}
                  >
                    <span className="material-icons" style={{ fontSize: '18px' }}>delete</span>
                  </button>
                </div>
              )}

              {/* Post Content */}
              {post.content && (
                <div style={{ marginBottom: '12px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                  {post.content}
                </div>
              )}

              {/* Post Media */}
              {post.media_urls && post.media_urls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: post.media_urls.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  {post.media_urls.map((media, index) => (
                    <div key={index}>
                      <MessageMedia
                        mediaType={media.mediaType as 'image' | 'video'}
                        mediaUrl={media.url}
                        thumbnailUrl={media.thumbnailUrl}
                        allowDownload={false}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Reaction & Comment Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '1px solid var(--border-color)',
                marginTop: '12px'
              }}>
                <button
                  className="btn btn-text"
                  onClick={() => handleReaction(post.id, 'like')}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span className="material-icons">thumb_up</span>
                  <span>{post.reaction_count > 0 ? post.reaction_count : 'Like'}</span>
                </button>
                <button
                  className="btn btn-text"
                  onClick={() => toggleComments(post.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span className="material-icons">comment</span>
                  <span>{post.comment_count > 0 ? post.comment_count : 'Comment'}</span>
                </button>
              </div>

              {/* Comments Section */}
              {expandedComments.has(post.id) && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                  {comments[post.id]?.map((comment) => (
                    <div key={comment.id} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      {comment.photo_url ? (
                        <img
                          src={comment.photo_url}
                          alt={comment.display_name}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'var(--primary-color)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {comment.display_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          background: 'var(--background-default)',
                          padding: '8px 12px',
                          borderRadius: '12px'
                        }}>
                          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                            {comment.display_name}
                          </div>
                          <div style={{ fontSize: '14px' }}>{comment.content}</div>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', marginLeft: '12px' }}>
                          {formatTime(comment.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add Comment */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    {loginUser?.photo_url ? (
                      <img
                        src={loginUser.photo_url}
                        alt={loginUser.display_name}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: 'var(--primary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {loginUser?.display_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="textfield-input"
                        placeholder="Write a comment..."
                        value={newComment[post.id] || ''}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddComment(post.id);
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '18px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--background-default)'
                        }}
                      />
                      <button
                        className="icon-btn"
                        onClick={() => handleAddComment(post.id)}
                        disabled={!newComment[post.id]?.trim()}
                      >
                        <span className="material-icons">send</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* Create Post Modal */}
      {showPostModal && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000
            }}
            onClick={() => setShowPostModal(false)}
          />
          <div
            className="paper auth-form-container"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              backgroundColor: '#ffffff'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5em', color: '#000000' }}>Create Post</h2>
              <button className="icon-btn" onClick={() => setShowPostModal(false)} style={{ color: '#000000' }}>
                <span className="material-icons">close</span>
              </button>
            </div>

            <textarea
              placeholder="What's on your mind?"
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--background-default)',
                color: 'var(--text-primary)',
                fontSize: '16px',
                resize: 'vertical',
                marginBottom: '16px'
              }}
            />

            {/* Media Preview */}
            {previewUrls.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: previewUrls.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: '8px',
                marginBottom: '16px'
              }}>
                {previewUrls.map((url, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    {selectedFiles[index].type.startsWith('image/') ? (
                      <img src={url} alt="Preview" style={{ width: '100%', borderRadius: '8px' }} />
                    ) : (
                      <video src={url} style={{ width: '100%', borderRadius: '8px' }} controls />
                    )}
                    <button
                      onClick={() => {
                        const newFiles = selectedFiles.filter((_, i) => i !== index);
                        const newUrls = previewUrls.filter((_, i) => i !== index);
                        URL.revokeObjectURL(url);
                        setSelectedFiles(newFiles);
                        setPreviewUrls(newUrls);
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                    >
                      <span className="material-icons">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <button
                className="btn btn-outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ flex: '1 1 140px', minWidth: 0 }}
              >
                <span className="material-icons" style={{ marginRight: '8px', flexShrink: 0 }}>photo_library</span>
                <span style={{ whiteSpace: 'nowrap' }}>Add Photos/Videos</span>
              </button>
              <button
                className="btn btn-contained"
                onClick={handleCreatePost}
                disabled={(!newPostContent.trim() && selectedFiles.length === 0) || uploading}
                style={{ flex: '1 1 80px', minWidth: 0 }}
              >
                {uploading ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
